<script setup lang="ts">
import { TASK_STEP_LABEL_MAX, TASK_STEPS_MAX, checklistProgress, dropTargetIndex } from '@cockpit/shared';
import { computed, nextTick, ref } from 'vue';
import '../../styles/checklist.css';

/**
 * Lot AS [16] : « étapes » d'une carte « Tâche » (maquette `retours-1410.html`, onglet C). On coche, on ajoute
 * avec Entrée, on modifie d'un clic (Entrée valide, Échap annule), on supprime avec la croix, on range avec la
 * poignée ⋮⋮ (glisser-déposer, ou Alt + ↑/↓ au clavier). Ce qui est fait est barré.
 *
 * Le composant ne garde rien : il remonte chaque geste, que le parent enregistre (carte dépliée : une route
 * par geste) ou applique à son brouillon (« Nouvelle tâche » : `checkable` faux, pas de case ni d'avancement).
 */
const props = withDefaults(
  defineProps<{
    items: readonly { id: string; label: string; done: boolean }[];
    /** Cases à cocher, compteur et barre d'avancement (carte dépliée). */
    checkable?: boolean;
    disabled?: boolean;
    /** Lot AW : étapes changées hors connexion, pas encore envoyées (« en attente d'envoi »). */
    pending?: ReadonlySet<string>;
  }>(),
  { checkable: true, disabled: false, pending: undefined },
);
const emit = defineEmits<{
  add: [label: string];
  toggle: [id: string, done: boolean];
  rename: [id: string, label: string];
  remove: [id: string];
  move: [id: string, to: number];
}>();

const progress = computed(() => checklistProgress(props.items));
const full = computed(() => props.items.length >= TASK_STEPS_MAX);
const root = ref<HTMLElement | null>(null);

// ------------------------------------------------------------------ Ajouter
const newLabel = ref('');
function add(): void {
  const label = newLabel.value.trim();
  if (!label || full.value) return;
  emit('add', label);
  newLabel.value = '';
}

// ------------------------------------------------------------------ Modifier d'un clic
const editingId = ref<string | null>(null);
const editText = ref('');
async function startEdit(item: { id: string; label: string }): Promise<void> {
  if (props.disabled) return;
  editingId.value = item.id;
  editText.value = item.label;
  await nextTick();
  const input = root.value?.querySelector<HTMLInputElement>(`[data-edit-id="${item.id}"]`);
  input?.focus();
  input?.select();
}
function commitEdit(): void {
  const id = editingId.value;
  if (!id) return;
  editingId.value = null;
  const label = editText.value.trim();
  const item = props.items.find((i) => i.id === id);
  if (item && label && label !== item.label) emit('rename', id, label);
}
function cancelEdit(): void {
  editingId.value = null;
}

// ------------------------------------------------------------------ Ranger : glisser-déposer et clavier
const dragId = ref<string | null>(null);
const armedId = ref<string | null>(null);
const dropAt = ref<{ id: string; after: boolean } | null>(null);

function onDragStart(event: DragEvent, id: string): void {
  dragId.value = id;
  try {
    event.dataTransfer?.setData('text/plain', id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  } catch {
    // Certains navigateurs refusent setData : l'identifiant reste dans `dragId`.
  }
}
function onDragOver(event: DragEvent, id: string): void {
  if (!dragId.value) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
  dropAt.value = { id, after: event.clientY > box.top + box.height / 2 };
}
function onDrop(event: DragEvent): void {
  event.preventDefault();
  const id = dragId.value;
  const target = dropAt.value;
  onDragEnd();
  if (!id || !target) return;
  const from = props.items.findIndex((i) => i.id === id);
  const over = props.items.findIndex((i) => i.id === target.id);
  if (from < 0 || over < 0) return;
  const to = dropTargetIndex(from, over, target.after);
  if (to !== from) emit('move', id, to);
}
function onDragEnd(): void {
  dragId.value = null;
  armedId.value = null;
  dropAt.value = null;
}

async function onHandleKey(event: KeyboardEvent, id: string, index: number): Promise<void> {
  if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
  event.preventDefault();
  const to = event.key === 'ArrowUp' ? index - 1 : index + 1;
  if (to < 0 || to >= props.items.length) return;
  emit('move', id, to);
  // La poignée garde le focus après le déplacement (la liste est redessinée).
  await nextTick();
  root.value?.querySelector<HTMLButtonElement>(`[data-handle-id="${id}"]`)?.focus();
}
</script>

<template>
  <div ref="root" class="checklist" data-testid="checklist-editor">
    <template v-if="checkable">
      <div class="sec-h">
        <b>Étapes</b>
        <span v-if="progress.total > 0" data-testid="checklist-count"
          >{{ progress.done }} / {{ progress.total }}</span
        >
      </div>
      <div
        v-if="progress.total > 0"
        class="progress"
        :class="{ done: progress.complete }"
        role="progressbar"
        :aria-valuenow="progress.percent"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="`Avancement : ${progress.done} étapes sur ${progress.total}`"
        data-testid="checklist-progress"
      >
        <span :style="{ width: `${progress.percent}%` }"></span>
      </div>
    </template>
    <ul v-if="items.length > 0" class="check" :class="{ draft: !checkable }">
      <li
        v-for="(item, index) in items"
        :key="item.id"
        :class="{
          done: checkable && item.done,
          dragging: dragId === item.id,
          'drop-before': dropAt?.id === item.id && !dropAt.after && dragId !== item.id,
          'drop-after': dropAt?.id === item.id && dropAt.after && dragId !== item.id,
        }"
        :draggable="armedId === item.id ? 'true' : 'false'"
        :data-item-id="item.id"
        data-testid="checklist-item"
        @dragstart="onDragStart($event, item.id)"
        @dragover="onDragOver($event, item.id)"
        @drop="onDrop"
        @dragend="onDragEnd"
      >
        <button
          class="handle"
          type="button"
          :data-handle-id="item.id"
          :disabled="disabled || items.length < 2"
          :aria-label="`Déplacer l’étape « ${item.label} » (Alt + flèche haut ou bas)`"
          title="Glisser pour ranger (ou Alt + ↑/↓)"
          data-testid="checklist-handle"
          @mousedown="armedId = item.id"
          @touchstart.passive="armedId = item.id"
          @mouseup="armedId = dragId ? armedId : null"
          @keydown="onHandleKey($event, item.id, index)"
        >
          ⋮⋮
        </button>
        <input
          v-if="checkable"
          type="checkbox"
          :checked="item.done"
          :disabled="disabled"
          :aria-label="`${item.done ? 'Décocher' : 'Cocher'} « ${item.label} »`"
          data-testid="checklist-check"
          @change="emit('toggle', item.id, ($event.target as HTMLInputElement).checked)"
        />
        <span v-else class="dot" aria-hidden="true">•</span>
        <input
          v-if="editingId === item.id"
          v-model="editText"
          type="text"
          :maxlength="TASK_STEP_LABEL_MAX"
          :data-edit-id="item.id"
          aria-label="Modifier l’étape"
          data-testid="checklist-edit"
          @keydown.enter.prevent="commitEdit"
          @keydown.escape.prevent.stop="cancelEdit"
          @blur="commitEdit"
        />
        <button
          v-else
          class="txt"
          type="button"
          :disabled="disabled"
          :title="'Modifier'"
          data-testid="checklist-label"
          @click="startEdit(item)"
        >
          {{ item.label }}
        </button>
        <span v-if="pending?.has(item.id)" class="pending item-pending" data-testid="checklist-pending"
          ><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
            <circle cx="8" cy="8" r="6" />
            <path d="M8 4.5V8l2.5 1.5" stroke-linecap="round" /></svg
          >en attente d’envoi</span
        >
        <button
          class="del"
          type="button"
          :disabled="disabled"
          :aria-label="`Supprimer l’étape « ${item.label} »`"
          data-testid="checklist-delete"
          @click="emit('remove', item.id)"
        >
          ×
        </button>
      </li>
    </ul>
    <div class="add-item">
      <input
        v-model="newLabel"
        type="text"
        :maxlength="TASK_STEP_LABEL_MAX"
        :disabled="disabled || full"
        :placeholder="full ? `${TASK_STEPS_MAX} étapes au plus` : '+ Ajouter une étape (Entrée)'"
        aria-label="Ajouter une étape"
        data-testid="checklist-add"
        @keydown.enter.prevent="add"
      />
    </div>
    <p v-if="checkable && progress.complete" class="all-done" data-testid="checklist-all-done">
      Tout est coché. La carte reste là jusqu’à ce que tu cliques « Fait ».
    </p>
  </div>
</template>
